// Unified DateTime utilities for consistent handling across the application

/**
 * Formats a DateTime string from backend to German locale
 */
export const formatDateTime = (dateTime: string | Date): string => {
  if (!dateTime) return '-';
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Formats a DateTime string to German date format
 */
export const formatDate = (dateTime: string | Date): string => {
  if (!dateTime) return '-';
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Formats a DateTime string to German time format
 */
export const formatTime = (dateTime: string | Date): string => {
  if (!dateTime) return '-';
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Formats a DateTime string for HTML datetime-local input
 */
export const formatDateTimeForInput = (dateTime: string | Date): string => {
  if (!dateTime) return '';
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  if (isNaN(date.getTime())) return '';
  
  // Format: YYYY-MM-DDTHH:MM
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Formats a DateTime string for HTML date input
 */
export const formatDateForInput = (dateTime: string | Date): string => {
  if (!dateTime) return '';
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  if (isNaN(date.getTime())) return '';
  
  // Format: YYYY-MM-DD
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Formats a DateTime string for HTML time input
 */
export const formatTimeForInput = (dateTime: string | Date): string => {
  if (!dateTime) return '';
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  if (isNaN(date.getTime())) return '';
  
  // Format: HH:MM
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${hours}:${minutes}`;
};

/**
 * Converts HTML datetime-local input value to ISO string for backend
 */
export const parseInputDateTime = (inputValue: string): string => {
  if (!inputValue) return '';
  const date = new Date(inputValue);
  if (isNaN(date.getTime())) return '';
  
  return date.toISOString();
};

/**
 * Converts HTML date input value to ISO string for backend
 */
export const parseInputDate = (inputValue: string): string => {
  if (!inputValue) return '';
  const date = new Date(inputValue + 'T00:00:00.000Z');
  if (isNaN(date.getTime())) return '';
  
  return date.toISOString();
};

/**
 * Combines date and time input values to ISO string for backend
 */
export const combineDateTime = (dateValue: string, timeValue: string): string => {
  if (!dateValue || !timeValue) return '';
  const dateTime = new Date(`${dateValue}T${timeValue}:00.000Z`);
  if (isNaN(dateTime.getTime())) return '';
  
  return dateTime.toISOString();
};

/**
 * Calculates duration between two DateTime strings in minutes
 */
export const calculateDuration = (startTime: string, endTime: string): number => {
  if (!startTime || !endTime) return 0;
  const start = new Date(startTime);
  const end = new Date(endTime);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
};

/**
 * Formats duration in minutes to human readable format
 */
export const formatDuration = (minutes: number): string => {
  if (!minutes || minutes < 0) return '-';
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${mins}min`;
  }
  return `${mins}min`;
};

/**
 * Calculates delay between planned and actual time in minutes
 */
export const calculateDelay = (plannedTime: string, actualTime?: string): number => {
  if (!actualTime) return 0;
  return calculateDuration(plannedTime, actualTime);
};

/**
 * Formats delay with appropriate sign and color indication
 */
export const formatDelay = (delayMinutes: number): { text: string; color: string } => {
  if (delayMinutes === 0) {
    return { text: 'Pünktlich', color: '#28a745' };
  } else if (delayMinutes > 0) {
    return { text: `+${formatDuration(delayMinutes)} verspätet`, color: '#dc3545' };
  } else {
    return { text: `${formatDuration(Math.abs(delayMinutes))} früher`, color: '#007bff' };
  }
};

/**
 * Checks if a date is today
 */
export const isToday = (dateTime: string | Date): boolean => {
  if (!dateTime) return false;
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  if (isNaN(date.getTime())) return false;
  
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

/**
 * Checks if a date is in the past
 */
export const isPast = (dateTime: string | Date): boolean => {
  if (!dateTime) return false;
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  if (isNaN(date.getTime())) return false;
  
  return date.getTime() < Date.now();
};

/**
 * Checks if a date is in the future
 */
export const isFuture = (dateTime: string | Date): boolean => {
  if (!dateTime) return false;
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  if (isNaN(date.getTime())) return false;
  
  return date.getTime() > Date.now();
};

/**
 * Gets relative time description (e.g., "in 2 hours", "3 days ago")
 */
export const getRelativeTime = (dateTime: string | Date): string => {
  if (!dateTime) return '-';
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  if (isNaN(date.getTime())) return '-';
  
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffMinutes = Math.round(diffMs / (1000 * 60));
  const diffHours = Math.round(diffMs / (1000 * 60 * 60));
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  
  if (Math.abs(diffMinutes) < 60) {
    if (diffMinutes === 0) return 'jetzt';
    return diffMinutes > 0 ? `in ${diffMinutes} Min` : `vor ${Math.abs(diffMinutes)} Min`;
  } else if (Math.abs(diffHours) < 24) {
    return diffHours > 0 ? `in ${diffHours}h` : `vor ${Math.abs(diffHours)}h`;
  } else {
    return diffDays > 0 ? `in ${diffDays} Tagen` : `vor ${Math.abs(diffDays)} Tagen`;
  }
};

/**
 * Validates if a string is a valid DateTime
 */
export const isValidDateTime = (dateTime: string): boolean => {
  if (!dateTime) return false;
  const date = new Date(dateTime);
  return !isNaN(date.getTime());
};

/**
 * Gets current DateTime in ISO format for backend
 */
export const getCurrentDateTime = (): string => {
  return new Date().toISOString();
};

/**
 * Adds minutes to a DateTime string
 */
export const addMinutes = (dateTime: string, minutes: number): string => {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return '';
  
  date.setMinutes(date.getMinutes() + minutes);
  return date.toISOString();
};

/**
 * Subtracts minutes from a DateTime string
 */
export const subtractMinutes = (dateTime: string, minutes: number): string => {
  return addMinutes(dateTime, -minutes);
};
